import { useMemo } from 'react'
import type { SortColumnDef } from '@frontend/lib/filters'
import { useSort } from '@frontend/lib/filters'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ExternalLink, X } from 'lucide-react'
import type { ReviewsListResponse, Review, ReviewStatus, ValidityStatus } from '@common/types'
import { REPORT_TYPES, REVIEW_STATUSES, VALIDITY_STATUSES, REPORT_TYPE_LABELS } from '@common/types'
import { ReportTypeBadge } from '@frontend/components/ui/report-type-badge'
import { calculateValidityStatus, isBridgeLetterCurrentlyValid } from '@common/utils/validity'
import { useFilters, type FilterDefinition } from '@frontend/lib/filters'
import { FilterBar } from '@frontend/components/filters/FilterBar'
import { SearchFilter } from '@frontend/components/filters/SearchFilter'
import { MultiSelect } from '@frontend/components/ui/multi-select'
import { api } from '@frontend/lib/api'
import { formatDate, formatTime } from '@frontend/lib/utils'
import { AppLayout } from '@frontend/components/layout/AppLayout'
import { Button } from '@frontend/components/ui/button'
import { Card, CardContent } from '@frontend/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@frontend/components/ui/table'
import { Spinner } from '@frontend/components/ui/spinner'
import { ReviewStatusBadge, ValidityBadge } from '@frontend/components/ui/status-badge'

async function fetchReviews(): Promise<ReviewsListResponse> {
  return api.get('reviews').json<ReviewsListResponse>()
}

// Label maps for filter display
const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  pending: 'Pending',
  extracting: 'Extracting',
  ai_complete: 'Awaiting Review',
  complete: 'Approved',
  failed: 'Failed',
}

// Frontend-only status layering bridge-letter coverage on top of ValidityStatus
// (mirrors the Vendors page): an expired report kept usable by a currently-valid
// bridge letter reads as "Bridge Letter".
type ReviewValidityFilter = ValidityStatus | 'bridge_covered'

const VALIDITY_STATUS_LABELS: Record<ReviewValidityFilter, string> = {
  valid: 'Valid',
  caution: 'Caution',
  invalid: 'Invalid',
  unknown: 'Unknown',
  bridge_covered: 'Bridge Letter',
}

// Filter options
const REPORT_TYPE_OPTIONS = REPORT_TYPES.map((type) => ({
  value: type,
  label: REPORT_TYPE_LABELS[type],
}))

const REVIEW_STATUS_OPTIONS = REVIEW_STATUSES.map((status) => ({
  value: status,
  label: REVIEW_STATUS_LABELS[status],
}))

const VALIDITY_STATUS_OPTIONS: { value: ReviewValidityFilter; label: string }[] = [
  ...VALIDITY_STATUSES.map((status) => ({ value: status, label: VALIDITY_STATUS_LABELS[status] })),
  { value: 'bridge_covered', label: VALIDITY_STATUS_LABELS.bridge_covered },
]

// Enriched review type with calculated (bridge-aware) validity status
interface EnrichedReview extends Review {
  validityStatus: ReviewValidityFilter
}

export function Reviews() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reviews'],
    queryFn: fetchReviews,
  })

  // Filter out archived reviews from the default view and enrich with validity status
  const enrichedReviews = useMemo<EnrichedReview[]>(() => {
    const nonArchivedReviews = data?.reviews.filter((r) => !r.archivedAt) ?? []
    return nonArchivedReviews.map((review) => {
      const validity = calculateValidityStatus(review.auditPeriodEnd)
      // A currently-valid bridge letter over an expired report (day 0+) reads as
      // "Bridge Letter" — matching the row's ValidityBadge — but only on the
      // vendor's latest review (a superseded bridge letter keeps its base status).
      const reportExpired = validity.status !== 'unknown' && validity.daysRemaining <= 0
      const bridgeValid = review.bridgeLetter != null && isBridgeLetterCurrentlyValid(review.bridgeLetter)
      const validityStatus: ReviewValidityFilter =
        reportExpired && bridgeValid && (review.isLatestReview ?? true) ? 'bridge_covered' : validity.status
      return { ...review, validityStatus }
    })
  }, [data?.reviews])

  // Define filters with match functions
  const filterDefinitions = useMemo<FilterDefinition<EnrichedReview>[]>(
    () => [
      {
        id: 'search',
        label: 'Search',
        type: 'search',
        match: (review, value) => {
          const search = value as string
          if (!search) return true
          return (review.vendorName ?? '').toLowerCase().includes(search.toLowerCase())
        },
      },
      {
        id: 'reportType',
        label: 'Report Type',
        type: 'multiselect',
        options: REPORT_TYPE_OPTIONS,
        match: (review, value) => {
          const types = value as string[]
          if (types.length === 0) return true
          return review.reportType ? types.includes(review.reportType) : false
        },
      },
      {
        id: 'status',
        label: 'Status',
        type: 'multiselect',
        options: REVIEW_STATUS_OPTIONS,
        match: (review, value) => {
          const statuses = value as ReviewStatus[]
          if (statuses.length === 0) return true
          return statuses.includes(review.status)
        },
      },
      {
        id: 'validity',
        label: 'Validity',
        type: 'multiselect',
        options: VALIDITY_STATUS_OPTIONS,
        match: (review, value) => {
          const validities = value as ReviewValidityFilter[]
          if (validities.length === 0) return true
          return validities.includes(review.validityStatus)
        },
      },
    ],
    []
  )

  // Use the filtering hook
  const { filteredData, filterState, setFilter, clearAllFilters, activeFilterCount } = useFilters(
    enrichedReviews,
    filterDefinitions
  )

  const sortColumns = useMemo<SortColumnDef<EnrichedReview>[]>(
    () => [
      { id: 'vendor', accessor: (r) => r.vendorName },
      { id: 'created', accessor: (r) => r.createdAt },
    ],
    []
  )

  const { sortedData, toggleSort, isSorted } = useSort(filteredData, sortColumns, {
    field: 'created',
    direction: 'desc',
  })

  // Type-safe accessors for filter values
  const searchValue = (filterState.search as string) || ''
  const reportTypeValue = (filterState.reportType as string[]) || []
  const statusValue = (filterState.status as ReviewStatus[]) || []
  const validityValue = (filterState.validity as ReviewValidityFilter[]) || []

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Compliance Reviews</h1>
          <p className="mt-1 text-muted-foreground">View and manage vendor compliance report extractions</p>
        </div>

        {/* Main content card */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            {/* Filters */}
            {!isLoading && !error && (
              <>
                <FilterBar>
                  <SearchFilter
                    value={searchValue}
                    onChange={(value) => setFilter('search', value)}
                    placeholder="Search vendors..."
                  />
                  <MultiSelect
                    options={REPORT_TYPE_OPTIONS}
                    selected={reportTypeValue}
                    onChange={(value) => setFilter('reportType', value)}
                    placeholder="Report type..."
                    searchPlaceholder="Search types..."
                    emptyMessage="No types found."
                    className="w-44"
                  />
                  <MultiSelect
                    options={REVIEW_STATUS_OPTIONS}
                    selected={statusValue}
                    onChange={(value) => setFilter('status', value)}
                    placeholder="Status..."
                    searchPlaceholder="Search statuses..."
                    emptyMessage="No statuses found."
                    className="w-40"
                  />
                  <MultiSelect
                    options={VALIDITY_STATUS_OPTIONS}
                    selected={validityValue}
                    onChange={(value) => setFilter('validity', value)}
                    placeholder="Validity..."
                    searchPlaceholder="Search validity..."
                    emptyMessage="No options found."
                    className="w-36"
                  />
                  {activeFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllFilters}
                      className="h-9 px-2 text-muted-foreground"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear filters
                    </Button>
                  )}
                </FilterBar>

                {/* Results count */}
                <div className="text-sm text-muted-foreground mt-4">
                  {sortedData.length} of {enrichedReviews.length} review{enrichedReviews.length !== 1 ? 's' : ''}
                  {activeFilterCount > 0 && ' (filtered)'}
                </div>
              </>
            )}

            {isLoading ? (
              <div className="flex justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : error ? (
              <div className="py-16 text-center">
                <p className="text-destructive">Failed to load reviews.</p>
                <p className="mt-1 text-sm text-muted-foreground">Please try refreshing the page.</p>
              </div>
            ) : sortedData.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-muted-foreground">
                  {activeFilterCount > 0 ? 'No reviews match your filters.' : 'No compliance reviews found.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <SortableTableHead
                        className="w-[200px]"
                        sorted={isSorted('vendor')}
                        onSort={() => toggleSort('vendor')}
                      >
                        Vendor
                      </SortableTableHead>
                      <TableHead className="w-[140px]">Report Type</TableHead>
                      <TableHead className="w-[120px]">Status</TableHead>
                      <TableHead className="w-[220px]">Validity</TableHead>
                      <SortableTableHead
                        className="w-[180px]"
                        sorted={isSorted('created')}
                        onSort={() => toggleSort('created')}
                      >
                        Created
                      </SortableTableHead>
                      <TableHead className="w-[80px] text-right">Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedData.map((review) => (
                      <TableRow key={review.id} className="group">
                        <TableCell className="font-medium">
                          <Link to={`/reviews/${review.id}`} className="hover:underline">
                            {review.vendorName || <span className="text-muted-foreground">Unknown</span>}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <ReportTypeBadge reportType={review.reportType} />
                        </TableCell>
                        <TableCell>
                          <ReviewStatusBadge status={review.status} />
                        </TableCell>
                        <TableCell>
                          <ValidityBadge
                            auditPeriodEnd={review.auditPeriodEnd}
                            bridgeLetter={review.bridgeLetter}
                            isLatestReview={review.isLatestReview}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{formatDate(review.createdAt)}</div>
                            <div className="text-muted-foreground">{formatTime(review.createdAt)}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {review.sourceUrl ? (
                            <a
                              href={review.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
